import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import api, { inr, formatApiError } from "../lib/api";
import { AlertTriangle, Upload, FileSpreadsheet, Pencil, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

const btn = "h-10 px-4 rounded-md border border-input bg-card text-sm flex items-center gap-2 hover:bg-muted transition-colors disabled:opacity-50";

export const AccountUploadButtons = ({ partyId, title, onDone }) => {
  const ref = useRef(null);
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  const url = `/parties/${partyId}/import-ledger`;
  const template = async () => {
    try {
      const res = await api.get("/ledger/import-template", { params: { kind: "single" }, responseType: "blob" });
      const u = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = u; a.download = "ledger_import_template.xlsx"; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Could not download template"); }
  };
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post(url, fd, { params: { preview: true }, headers: { "Content-Type": "multipart/form-data" } });
      setPv({ data, file: f });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const save = async (skip, rows) => {
    const fd = new FormData();
    fd.append("file", pv.file);
    if (rows) fd.append("rows_json", JSON.stringify(rows));
    try {
      const { data } = await api.post(url, fd, { params: { skip_duplicates: !!skip }, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Saved: ${data.debit_rows} due(s), ${data.credit_rows} payment(s)`);
      setPv(null); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <>
      <button data-testid="ledger-upload-template" onClick={template} className={btn}><FileSpreadsheet size={15} /> Template</button>
      <button data-testid="ledger-upload-btn" disabled={busy} onClick={() => ref.current?.click()} className={btn}><Upload size={15} /> {busy ? "Reading…" : "Bulk upload"}</button>
      <input ref={ref} data-testid="ledger-upload-input" type="file" accept=".csv,.xlsx" className="hidden" onChange={pick} />
      {pv && <LedgerImportPreview preview={pv.data} bulk={false} title={title} onCancel={() => setPv(null)} onConfirm={save} />}
    </>
  );
};

const Totals = ({ p }) => (
  <div className="grid grid-cols-3 gap-2 text-center">
    {[["Dues (debit)", p.total_debit, "pv-total-debit"], ["Payments (credit)", p.total_credit, "pv-total-credit"], ["Net balance", p.net, "pv-net"]].map(([l, v, id]) => (
      <div key={id} className="rounded-md border border-border bg-muted/40 px-2 py-2">
        <div className="text-[11px] text-muted-foreground">{l}</div>
        <div data-testid={id} className="num font-bold text-sm">{inr(v)}</div>
      </div>
    ))}
  </div>
);

const cell = "h-7 w-full px-1.5 rounded border border-input bg-card text-xs";

const EditRow = ({ r, bulk, onSave, onCancel }) => {
  const [v, setV] = useState(r);
  const inp = (k, cls = "", type = "text") => <input data-testid={`pv-edit-${k}-${r.row}`} type={type} className={`${cell} ${cls}`} value={v[k] ?? ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} />;
  return (
    <tr className="border-t border-border bg-primary/5">
      <td className="px-2 py-1 num text-muted-foreground">{r.row}</td>
      {bulk && <td className="px-1 py-1">{inp("party")}</td>}
      {bulk && <td className="px-1 py-1">{inp("account")}</td>}
      <td className="px-1 py-1">{inp("date", "num", "date")}</td>
      <td className="px-1 py-1">{inp("invoice_no", "num")}</td>
      <td className="px-1 py-1">{inp("particulars")}</td>
      <td className="px-1 py-1">{inp("debit", "num text-right", "number")}</td>
      <td className="px-1 py-1">{inp("credit", "num text-right", "number")}</td>
      <td className="px-1 py-1 whitespace-nowrap">
        <button data-testid={`pv-edit-ok-${r.row}`} onClick={() => onSave({ ...v, debit: parseFloat(v.debit) || 0, credit: parseFloat(v.credit) || 0 })} className="p-1 text-primary hover:bg-primary/10 rounded"><Check size={13} /></button>
        <button onClick={onCancel} className="p-1 text-muted-foreground hover:bg-muted rounded text-[10px]">Esc</button>
      </td>
    </tr>
  );
};

const RowsTable = ({ rows, bulk, onEdit, onDelete }) => {
  const [editing, setEditing] = useState(null);
  return (
  <div className="max-h-72 overflow-auto rounded-md border border-border">
    <table className="w-full text-xs table-fixed min-w-[640px]">
      <colgroup>
        <col className="w-[70px]" />{bulk && <col className="w-[16%]" />}{bulk && <col className="w-[16%]" />}
        <col className="w-[130px]" /><col className="w-[90px]" /><col /><col className="w-[90px]" /><col className="w-[90px]" /><col className="w-[60px]" />
      </colgroup>
      <thead className="bg-muted sticky top-0 z-10">
        <tr className="text-left">
          <th className="px-2 py-1.5">Row</th>
          {bulk && <th className="px-2 py-1.5">Party</th>}
          {bulk && <th className="px-2 py-1.5">Account</th>}
          <th className="px-2 py-1.5">Date</th><th className="px-2 py-1.5">Invoice no.</th><th className="px-2 py-1.5">Particulars</th>
          <th className="px-2 py-1.5 text-right">Debit</th><th className="px-2 py-1.5 text-right">Credit</th><th className="px-2 py-1.5"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => editing === r.row ? (
          <EditRow key={r.row} r={r} bulk={bulk} onCancel={() => setEditing(null)} onSave={(nr) => { onEdit(nr); setEditing(null); }} />
        ) : (
          <tr key={r.row} data-testid={`pv-row-${r.row}`} className={`border-t border-border align-top ${r.duplicate ? "bg-amber-500/10" : ""}`}>
            <td className="px-2 py-1 num text-muted-foreground">{r.row}{r.duplicate && <div data-testid={`pv-dup-${r.row}`} className="text-[9px] font-semibold text-amber-700">ALREADY UPLOADED</div>}</td>
            {bulk && <td className="px-2 py-1 break-words">{r.party}</td>}
            {bulk && <td className="px-2 py-1 break-words">{r.account}</td>}
            <td className="px-2 py-1 num whitespace-nowrap">{r.date}</td>
            <td className="px-2 py-1 num break-words">{r.invoice_no || (r.debit ? <span className="text-muted-foreground">auto</span> : "")}</td>
            <td className="px-2 py-1 break-words">{r.particulars}</td>
            <td className="px-2 py-1 num text-right">{r.debit ? inr(r.debit) : ""}</td>
            <td className="px-2 py-1 num text-right">{r.credit ? inr(r.credit) : ""}</td>
            <td className="px-1 py-1 whitespace-nowrap">
              <button data-testid={`pv-edit-${r.row}`} onClick={() => setEditing(r.row)} className="p-1 text-muted-foreground hover:text-primary rounded"><Pencil size={12} /></button>
              <button data-testid={`pv-del-${r.row}`} onClick={() => onDelete(r.row)} className="p-1 text-muted-foreground hover:text-destructive rounded"><Trash2 size={12} /></button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};

const totalsOf = (rows) => {
  const td = Math.round(rows.reduce((s, r) => s + (r.debit || 0), 0) * 100) / 100;
  const tc = Math.round(rows.reduce((s, r) => s + (r.credit || 0), 0) * 100) / 100;
  return { total_debit: td, total_credit: tc, net: Math.round((td - tc) * 100) / 100 };
};

export const LedgerImportPreview = ({ preview, bulk, title, onCancel, onConfirm }) => {
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(preview.rows);
  const [changed, setChanged] = useState(false);
  const dups = rows.filter((r) => r.duplicate).length;
  const [skip, setSkip] = useState(true);
  const total = changed ? rows.length : preview.row_count;
  const saving = total - (skip ? dups : 0);
  const kept = () => rows.filter((r) => !(skip && r.duplicate));
  const go = async () => { setBusy(true); await onConfirm(skip && dups > 0, changed ? kept() : null); setBusy(false); };
  const edit = (nr) => { setRows(rows.map((r) => (r.row === nr.row ? { ...nr, duplicate: false } : r))); setChanged(true); };
  const del = (n) => { setRows(rows.filter((r) => r.row !== n)); setChanged(true); };
  const p = changed ? { ...preview, ...totalsOf(rows) } : preview;
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent data-testid="ledger-preview-dialog" className="bg-card max-w-5xl w-[calc(100%-1rem)] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="min-w-0"><DialogTitle className="font-display text-xl sm:text-2xl truncate">Check before saving · {title}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm min-w-0">
          <p className="text-muted-foreground"><b className="text-foreground" data-testid="pv-row-count">{total}</b> row(s) ready{bulk ? ` across ${preview.accounts.length} account(s)` : ""}. Fix or remove rows with the pencil / bin. Nothing is saved until you confirm. These entries go only to the account statement, not the cash book.</p>
          <Totals p={p} />
          {dups > 0 && (
            <div data-testid="pv-dup-warning" className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs space-y-2">
              <div className="font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle size={13} /> {dups} row(s) look already uploaded (same account, party, date and amount)</div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input data-testid="pv-skip-dups" type="checkbox" checked={skip} onChange={(e) => setSkip(e.target.checked)} />
                Skip these rows so they aren't entered twice
              </label>
            </div>
          )}
          {bulk && preview.accounts.length > 1 && (
            <div data-testid="pv-accounts" className="flex flex-wrap gap-1.5 max-h-24 overflow-auto">
              {preview.accounts.map((a) => <span key={a.account} className="text-[11px] bg-primary/10 text-primary rounded px-2 py-0.5">{a.account}: {a.rows} row(s) · net {inr(a.debit - a.credit)}</span>)}
            </div>
          )}
          {rows.length > 0 && <RowsTable rows={rows} bulk={bulk} onEdit={edit} onDelete={del} />}
          {preview.errors.length > 0 && (
            <div data-testid="pv-errors" className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs max-h-28 overflow-auto">
              <div className="font-semibold text-destructive flex items-center gap-1 mb-1"><AlertTriangle size={13} /> {preview.errors.length} row(s) will be skipped</div>
              {preview.errors.map((e) => <div key={e}>{e}</div>)}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 min-w-0">
          <button data-testid="pv-cancel" onClick={onCancel} className="h-10 px-4 rounded-md border border-input bg-card text-sm hover:bg-muted">Cancel</button>
          <button data-testid="pv-confirm" disabled={busy || saving <= 0} onClick={go} className="h-10 px-5 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">{busy ? "Saving…" : `Save ${saving} row(s)`}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
